import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  createQuoteRequestApi,
  QuoteRequestStop,
  ShipperQuoteRequest,
  updateShipperQuoteRequestApi,
  VolumeFormat,
} from "api/rest/quote-requests";
import { QuoteRequestPageSteps } from "app/pages/request-management/types/QuoteRequestPageSteps";
import { DriverPreference, ShipmentPackagingType, ShipmentWeightUnit } from "app/pages/shipments/types/domain";
import { CargoGroup, CargoGroupType } from "features/cargo-groups/api/types";
import set from "lodash/set";
import { ModeQuotes } from "types/Mode";
import { TrailerTypeEnum } from "types/TrailerType";
import { UnloadingDetailsEnum } from "types/UnloadingDetails";
import usableActions from "utils/store/usableActions";

import { QuoteStepIndex } from "../types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DeepPartial<T> = T extends Record<any, any>
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

type QuoteRequestState = Partial<
  Omit<ShipperQuoteRequest, "partners" | "teammates" | "stops"> & {
    partners: Array<{ contact_id: number }>;
    teammates: Array<{ contact_id: number }>;
    stops: DeepPartial<QuoteRequestStop>[];
    // cargo_groups: CreateCargoGroup[];
  }
>;

type State = {
  selectedMode: ModeQuotes;
  quoteRequest: QuoteRequestState;
  completedStep?: QuoteRequestPageSteps;
  templateSearch?: string;
  stepIndex: QuoteStepIndex;
  createTemplate?: boolean;
  templateName?: string;
};

const mapModeToDefaults: Record<ShipperQuoteRequest["mode"], QuoteRequestState> = {
  FTL: {
    mode: "FTL",
    status: "DRAFT",
    load_spec: {
      driver_preference: DriverPreference.Solo,
      trailer_type: TrailerTypeEnum.DRY_VAN,
      weight_unit: ShipmentWeightUnit.Lb,
      truck_quantity: 1,
    },
    is_hazardous: false,
    stops: [{}, {}],
  },
  AIR: {
    mode: "AIR",
    stops: [{}, {}],
    status: "DRAFT",
    load_spec: {
      air_package_groups: [
        { volume_format: VolumeFormat.Dimensions, packaging_type: ShipmentPackagingType.Cartons, item_quantity: 1 },
      ],
    },
  },
  DRAYAGE: {
    mode: "DRAYAGE",
    insurance_required: false,
    include_customs_clearance: false,
    is_hazardous: false,
    status: "DRAFT",
    is_overweight: false,
    stops: [{}, {}],
    cargo_groups: [{ cargo_group_type: CargoGroupType.CARGO } as CargoGroup],
  },
  FCL: {
    mode: "FCL",
    stops: [{}, {}, {}, {}],
    is_hazardous: false,
    is_overweight: false,
    unloading_details: UnloadingDetailsEnum.LIVE,
    status: "DRAFT",
    load_spec: {
      is_overweight: false,
      is_palletized: true,
      include_customs_clearance: false,
      containers: [{}],
    },
  },
  LTL: {
    mode: "LTL",
    stops: [{}, {}],
    is_hazardous: false,
    status: "DRAFT",
  },
};

const initialState: State = {
  selectedMode: "FTL",
  quoteRequest: mapModeToDefaults["FTL"],
  completedStep: undefined,
  stepIndex: QuoteStepIndex.Location,
};

const quoteRequestSlice = createSlice({
  name: "quoteRequestSliceV2",
  initialState,
  reducers: {
    // KEEP: Mode selection (actively used)
    setSelectedMode: (state, action: PayloadAction<ModeQuotes>) => {
      state.selectedMode = action.payload;
    },
    setTemplateSearch: (state, action: PayloadAction<string>) => {
      state.templateSearch = action.payload;
    },
    setStepIndex: (state, action: PayloadAction<number>) => {
      state.stepIndex = action.payload;
    },
    setQuoteRequest: (state, action: PayloadAction<QuoteRequestState>) => {
      if (!action.payload) {
        return;
      }

      if (action.payload.mode && action.payload.mode !== state.quoteRequest.mode) {
        state.quoteRequest = mapModeToDefaults[action.payload.mode];
        state.stepIndex = 0;
      }

      state.quoteRequest = {
        ...state.quoteRequest,
        ...action.payload,
        load_spec: {
          ...state.quoteRequest.load_spec,
          ...action.payload.load_spec,
        },
      };
    },

    setQuoteRequestByPath: (
      state,
      action: PayloadAction<{
        path: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        value: any;
      }>
    ) => {
      set(state.quoteRequest, action.payload.path, action.payload.value);
    },

    // KEEP: ftlSlice and drayage reducers (actively used)
    resetState: () => {
      return initialState;
    },

    setState: (state, action: PayloadAction<Partial<State>>) => {
      return { ...state, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      updateShipperQuoteRequestApi.endpoints.updateShipperQuoteRequest.matchFulfilled,
      (state, action) => {
        action.payload.data.quoteRequest.status === "DRAFT" &&
          quoteRequestSlice.caseReducers.setQuoteRequest(
            state,
            quoteRequestSlice.actions.setQuoteRequest(action.payload.data.quoteRequest)
          );
      }
    );
    builder.addMatcher(createQuoteRequestApi.endpoints.createQuoteRequest.matchFulfilled, (state, action) => {
      // If we passed in a quote request id then we were creating from a template
      if (!!action.meta.arg.originalArgs.body.quoteRequestId) {
        quoteRequestSlice.caseReducers.setQuoteRequest(
          state,
          quoteRequestSlice.actions.setQuoteRequest(action.payload.data.quoteRequest)
        );
      }
    });
  },
});

export const quoteRequestActions = usableActions(quoteRequestSlice.actions);
export default quoteRequestSlice;
